import React, { useState, useEffect } from "react";
import {
  TextInput,
  Textarea,
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
  Select,
  Title,
} from "@mantine/core";
import { AppModal, AppIcon } from "@/components/generic";
import { Client, ClientInput } from "@/utils/api/ClientService";
import { StatusSelector } from "../../StatusSelector";
import { ClientTypeSelector } from "../ClientTypeSelector";

export interface ClientFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Client to edit (if null/undefined, it's create mode) */
  client?: Client | null;

  /** Callback when client should be created */
  onCreateClient?: (clientData: ClientInput) => Promise<Client | null>;

  /** Callback when client should be updated */
  onUpdateClient?: (
    client: Client,
    clientData: ClientInput
  ) => Promise<Client | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from client operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface ClientFormData {
  clientType: string;
  companyName: string;
  nameTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  website: string;
  notes: string;
  status: number;
}

const initialFormData: ClientFormData = {
  clientType: "",
  companyName: "",
  nameTitle: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  website: "",
  notes: "",
  status: 100,
};

const NAME_TITLES = [
  { value: "Mr.", label: "Mr." },
  { value: "Mrs.", label: "Mrs." },
  { value: "Ms.", label: "Ms." },
  { value: "Dr.", label: "Dr." },
  { value: "Prof.", label: "Prof." },
];

export const ClientFormModal: React.FC<ClientFormModalProps> = ({
  opened,
  onClose,
  client,
  onCreateClient,
  onUpdateClient,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<ClientFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});

  const isEditMode = Boolean(client) && Boolean(client?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or client changes
  useEffect(() => {
    if (opened) {
      if (client) {
        // Edit mode - populate form with client data
        setFormData({
          clientType: client.clientType || "",
          companyName: client.companyName || "",
          nameTitle: client.nameTitle || "",
          firstName: client.firstName || "",
          lastName: client.lastName || "",
          email: client.email || "",
          phone: client.phone || "",
          website: client.website || "",
          notes: client.notes || "",
          status: client.status != null ? client.status : 100,
        });
      } else {
        // Create mode - reset to initial values
        setFormData(initialFormData);
      }
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, client, onClearError]);

  const validateForm = (): boolean => {
    const newErrors: Partial<ClientFormData> = {};

    // At least one identifier is required
    const hasCompanyName = formData.companyName.trim();
    const hasPersonName = formData.firstName.trim() || formData.lastName.trim();

    if (!hasCompanyName && !hasPersonName) {
      if (!hasCompanyName) {
        newErrors.companyName =
          "Either company name or person name is required";
      }
      if (!hasPersonName) {
        newErrors.firstName = "Either company name or person name is required";
      }
    }

    if (!formData.email) {
      newErrors.email = "Email address is required";
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Website validation
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = "Website must start with http:// or https://";
    }

    // Phone validation (basic check for non-empty if provided)
    if (formData.phone && formData.phone.trim().length < 3) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof ClientFormData,
    value: string | number | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: ["status"].includes(field) ? Number(value) : value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Get the error to display for a field
  const getDisplayError = (
    field: keyof ClientFormData
  ): string | number | undefined => {
    return validationErrors[field];
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare client data
      const clientData: ClientInput = {
        clientType: formData.clientType || undefined,
        companyName: formData.companyName || undefined,
        nameTitle: formData.nameTitle || undefined,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        website: formData.website || undefined,
        notes: formData.notes || undefined,
        status: formData.status,
      };

      let result: Client | null = null;

      if (isEditMode && client && onUpdateClient) {
        // Update existing client
        result = await onUpdateClient(client, clientData);
      } else if (!isEditMode && onCreateClient) {
        // Create new client
        result = await onCreateClient(clientData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render generic errors at the bottom of the form
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Client" : "Add New Client"}
      size="lg"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Confirm",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Client Type */}
        <Grid>
          <Grid.Col span={12}>
            <ClientTypeSelector
              label="Client Type"
              placeholder="Select client type"
              value={formData.clientType}
              onChange={(value) => handleInputChange("clientType", value)}
              error={getDisplayError("clientType")}
              required
            />
          </Grid.Col>
        </Grid>

        {/* Company Information */}
        {formData.clientType !== "individual" && (
          <Grid>
            <Grid.Col span={12}>
              <Title order={5} mb="sm">
                Company Information
              </Title>
            </Grid.Col>

            <Grid.Col span={12}>
              <TextInput
                label="Company Name"
                placeholder="Enter company name"
                value={formData.companyName}
                onChange={(e) =>
                  handleInputChange("companyName", e.target.value)
                }
                error={getDisplayError("companyName")}
                required={formData.clientType !== "individual"}
                size="md"
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <TextInput
                label="Website"
                placeholder="https://example.com"
                value={formData.website}
                onChange={(e) => handleInputChange("website", e.target.value)}
                error={getDisplayError("website")}
                size="md"
              />
            </Grid.Col>
          </Grid>
        )}

        {/* Personal Information */}
        <Grid>
          <Grid.Col span={12}>
            <Title order={5} mb="sm">
              Contact Person
            </Title>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Select
              label="Title"
              placeholder="Select title"
              value={formData.nameTitle}
              onChange={(value) => handleInputChange("nameTitle", value)}
              data={NAME_TITLES}
              error={getDisplayError("nameTitle")}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 5 }}>
            <TextInput
              label="First Name"
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              error={getDisplayError("firstName")}
              required
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 5 }}>
            <TextInput
              label="Last Name"
              placeholder="Enter last name"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              error={getDisplayError("lastName")}
              required
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Contact Information */}
        <Grid>
          <Grid.Col span={12}>
            <Title order={5} mb="sm">
              Contact Information
            </Title>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              error={getDisplayError("email")}
              type="email"
              required
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Phone"
              placeholder="Enter phone number"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              error={getDisplayError("phone")}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Notes"
              placeholder="Enter any additional notes"
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              error={getDisplayError("notes")}
              rows={3}
              autosize
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Status */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <StatusSelector
                value={formData.status}
                onChange={(value) => handleInputChange("status", value)}
                error={getDisplayError("status")}
                required
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
